import { storeEvents, EVENTS } from "../common/stores/storeEvents.js";
import { loadTabContent, injectTabContent, preloadTabContent } from "./tabContentLoader.js";
import { logger } from "../utils/debug/logger.js";

// Map des noms conviviaux pour les tabs
const TAB_NAMES = {
  tab1: "Éventail",
  tab2: "Carte",
  tab3: "Tree",
  tab4: "Timeline",
  tab5: "Statistiques",
  tab6: "Personnes",
  tab7: "Photos",
  tab8: "Fusion",
};

// Flag de debug pour désactiver certains onglets temporairement
const DEBUG_DISABLED_TABS = new Set([
  // Onglets réactivés maintenant que le système de logging est en place
  // "tab2", // Carte
  // "tab5", // Statistiques
]);

// Garder une trace des onglets déjà initialisés
// ✅ OPTIMISATION: Utiliser sessionStorage pour survivre aux HMR (Hot Module Replacement)
// En développement, webpack peut recharger ce module, ce qui viderait un Set normal
// ✅ CORRECTION: Vider sessionStorage au chargement initial de la page pour garantir
// que la 1ère ouverture initialise vraiment l'arbre (pas de données stales d'une session précédente)
const STORAGE_KEY = 'geneafan_initialized_tabs';

// Clear sessionStorage on initial page load (not on HMR reloads)
if (!window.__geneafan_tabs_loaded) {
  sessionStorage.removeItem(STORAGE_KEY);
  window.__geneafan_tabs_loaded = true;
}

const initializedTabs = new Set(
  sessionStorage.getItem(STORAGE_KEY)?.split(',').filter(Boolean) || []
);

// Helper pour sauvegarder dans sessionStorage
function saveInitializedTabs() {
  sessionStorage.setItem(STORAGE_KEY, [...initializedTabs].join(','));
}

// Garder une trace des onglets dont le contenu a été chargé
const loadedTabs = new Set();

// État pour FanChartManager lazy loading
let fanChartManagerPromise = null;
let isFanChartInitialized = false;

// Exposer l'état globalement pour d'autres modules
window.isFanChartInitialized = false;

/**
 * Charge FanChartManager de manière lazy
 * @returns {Promise} Promise qui résout avec FanChartManager
 */
async function loadFanChartManager() {
  if (!fanChartManagerPromise) {
    logger.tabs.debug("🔄 Chargement dynamique de FanChartManager et D3.js...");
    // webpackPrefetch: précharge le chunk D3 pendant l'idle time du navigateur
    fanChartManagerPromise = import(/* webpackPrefetch: true */ "./fanChart/fanChartManager.js").then(module => {
      logger.tabs.debug("✅ FanChartManager et D3.js chargés");
      return module.FanChartManager;
    });
  }
  return fanChartManagerPromise;
}

/**
 * Initialise FanChartManager si ce n'est pas déjà fait
 */
async function initializeFanChartIfNeeded() {
  if (!isFanChartInitialized) {
    logger.tabs.debug("📊 Initialisation du FanChartManager...");
    const FanChartManager = await loadFanChartManager();
    await FanChartManager.initialize();
    isFanChartInitialized = true;
    window.isFanChartInitialized = true; // Exposer globalement
    logger.tabs.debug("✅ FanChartManager initialisé");
  }
}

function setupTabChangeTracking() {
  document.querySelectorAll('a[data-bs-toggle="tab"]').forEach((tabElement) => {
    tabElement.addEventListener("shown.bs.tab", (event) => {
      const targetElement = document.querySelector(
        event.target.getAttribute("href")
      );
      if (!targetElement) {
        logger.tabs.warn("❌ Élément cible non trouvé pour le tab");
        return;
      }

      const prevTargetElement = event.relatedTarget
        ? document.querySelector(event.relatedTarget.getAttribute("href"))
        : null;

      const newTabId = targetElement.id;
      const prevTabId = prevTargetElement?.id;

      const newTabName = TAB_NAMES[newTabId] || newTabId;
      const prevTabName = TAB_NAMES[prevTabId] || prevTabId || "aucun onglet";

      logger.tabs.group("📑 Changement d'onglet");
      logger.tabs.debug(`↪️ ${prevTabName} → ${newTabName}`);
      logger.tabs.groupEnd();

      // Émission des événements avec la nouvelle structure UI.TABS
      storeEvents.emit(EVENTS.UI.TABS.CHANGED, {
        newTab: { id: newTabId, name: newTabName },
        previousTab: { id: prevTabId, name: prevTabName },
      });
      storeEvents.emit(EVENTS.UI.TABS.SHOWN, {
        id: newTabId,
        name: newTabName,
      });

      if (prevTabId) {
        storeEvents.emit(EVENTS.UI.TABS.HIDDEN, {
          id: prevTabId,
          name: prevTabName,
        });
      }

      // Émettre aussi l'événement onboarding si nécessaire
      storeEvents.emit(EVENTS.ONBOARDING.TOUR_STARTED, { tabId: newTabId });
    });
  });
}

// Mise à jour des écouteurs d'événements pour utiliser UI.TABS avec lazy loading
storeEvents.subscribe(EVENTS.UI.TABS.SHOWN, async ({ id }) => {
  // Gérer l'onglet Fan
  if (id === "tab1") {
    // Si un fichier GEDCOM est chargé mais FanChartManager n'est pas encore initialisé
    // Nous devons importer rootPersonStore dynamiquement pour vérifier
    try {
      const { default: rootPersonStore } = await import("../common/stores/rootPersonStore.js");
      const root = rootPersonStore.root;
      if (root && !isFanChartInitialized) {
        logger.tabs.debug("🎯 Onglet Fan activé avec GEDCOM chargé, initialisation de FanChartManager...");
        await initializeFanChartIfNeeded();
        // Émettre à nouveau ROOT.CHANGED pour déclencher le dessin
        setTimeout(() => {
          storeEvents.emit(EVENTS.ROOT.CHANGED, { root, skipDraw: false });
        }, 100);
      }
    } catch (error) {
      logger.tabs.error("❌ Erreur lors du chargement de rootPersonStore:", error);
    }
  }
  
  if (id === "tab2") {
    logger.tabs.group("🗺️ Activation de la carte");
    try {
      // Import dynamique des modules nécessaires seulement quand l'onglet est actif
      const { googleMapsStore } = await import("./familyMap/stores/googleMapsStore.js");
      const { googleMapManager } = await import("./familyMap/managers/googleMapManager.js");
      const { rootAncestorTownsStore } = await import("./familyMap/stores/rootAncestorTownsStore.js");
      
      await googleMapsStore.resizeAndMoveMap();
      logger.tabs.debug("✅ Carte déplacée et redimensionnée");
      googleMapManager.setupEventListeners();
      if (rootAncestorTownsStore.hasActiveMarkers()) {
        rootAncestorTownsStore.centerMapOnAncestorMarkers();
      }
    } catch (error) {
      logger.tabs.error("❌ Erreur lors du chargement de la carte:", error);
    }
    logger.tabs.groupEnd();
  }
});

storeEvents.subscribe(EVENTS.UI.TABS.HIDDEN, async ({ id }) => {
  if (id === "tab2") {
    logger.tabs.debug(`🗺️ La carte est masquée, arrêt des mises à jour.`);
    try {
      // Import dynamique des modules nécessaires
      const { rootAncestorTownsStore } = await import("./familyMap/stores/rootAncestorTownsStore.js");
      const { default: familyTownsStore } = await import("./familyMap/stores/familyTownsStore.js");
      
      rootAncestorTownsStore.toggleVisibility(false);
      familyTownsStore.toggleVisibility(false);
    } catch (error) {
      logger.tabs.error("❌ Erreur lors de la désactivation des couches de la carte:", error);
    }
  }
});

async function initializeTabOnVisible(tabSelector, initCallback) {
  logger.tabs.group(`📑 Configuration de l'initialisation pour ${tabSelector}`);

  const tab = document.querySelector(tabSelector);
  if (!tab) {
    logger.tabs.warn(`⚠️ Onglet ${tabSelector} non trouvé`);
    logger.tabs.groupEnd();
    return;
  }

  logger.tabs.debug(`✓ Onglet ${tabSelector} trouvé, configuration de l'observer...`);

  const observer = new IntersectionObserver(async (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        logger.tabs.debug(
          `📍 Onglet ${tabSelector} visible, démarrage de l'initialisation`
        );
        
        const tabId = tabSelector.replace("#", "");
        
        // Vérifier si l'onglet est désactivé pour debug
        if (DEBUG_DISABLED_TABS.has(tabId)) {
          logger.tabs.debug(`🚫 Onglet ${tabId} désactivé pour debug - initialisation ignorée`);
          observer.disconnect();
          return;
        }
        
        try {
          // Charger le contenu HTML si pas encore fait
          if (!loadedTabs.has(tabId)) {
            logger.tabs.debug(`📄 Chargement du contenu HTML pour ${tabId}...`);
            const content = await loadTabContent(tabId);
            injectTabContent(tabId, content);
            loadedTabs.add(tabId);
            logger.tabs.debug(`✓ Contenu HTML chargé et injecté pour ${tabId}`);
          }

          // ✅ OPTIMISATION: Initialiser le JavaScript du tab SEULEMENT si pas déjà initialisé
          logger.tabs.debug(`🔍 Vérification initializedTabs pour ${tabId}: ${initializedTabs.has(tabId)} - Set contient: ${[...initializedTabs].join(', ')}`);
          if (!initializedTabs.has(tabId)) {
            logger.tabs.debug(`🚀 Initialisation du JavaScript pour ${tabId}...`);
            await initCallback();

            // Marquer l'onglet comme initialisé
            initializedTabs.add(tabId);
            saveInitializedTabs(); // Persister dans sessionStorage
            logger.tabs.debug(`✅ Onglet ${tabId} initialisé avec succès`);
          } else {
            logger.tabs.debug(`👀 Onglet ${tabId} déjà initialisé, ignoré`);
          }
          
          // Précharger le prochain onglet probable
          const nextTabId = getNextTabId(tabId);
          if (nextTabId && !loadedTabs.has(nextTabId)) {
            logger.tabs.debug(`🔄 Préchargement du prochain onglet: ${nextTabId}`);
            preloadTabContent(nextTabId);
          }
          
        } catch (error) {
          logger.tabs.error(
            `❌ Erreur lors de l'initialisation de ${tabSelector}:`,
            error
          );
        }

        // ✅ OPTIMISATION: Ne PAS déconnecter l'observer !
        // Il doit rester actif pour détecter les futures visibilités
        // La vérification initializedTabs empêche les réinitialisations
        // observer.disconnect();
        // logger.tabs.debug(`✅ Observer déconnecté pour ${tabSelector}`);
      }
    }
  });

  observer.observe(tab);
  logger.tabs.debug(`✓ Observer configuré pour ${tabSelector}`);
  logger.tabs.groupEnd();
}

// Fonction utilitaire pour déterminer le prochain onglet probable
function getNextTabId(currentTabId) {
  const tabOrder = ['tab1', 'tab2', 'tab3', 'tab4', 'tab5', 'tab6', 'tab7', 'tab8'];
  const currentIndex = tabOrder.indexOf(currentTabId);
  if (currentIndex !== -1 && currentIndex < tabOrder.length - 1) {
    return tabOrder[currentIndex + 1];
  }
  return null;
}


/**
 * Configure l'écouteur pour initialiser FanChartManager lors du premier ROOT.CHANGED
 */
function setupFanChartLazyLoading() {
  logger.tabs.debug("🔧 Configuration du lazy loading pour FanChartManager");
  
  // Écouter le premier changement de root pour initialiser FanChartManager
  const disposer = storeEvents.subscribe(EVENTS.ROOT.CHANGED, async ({ root, skipDraw }) => {
    logger.tabs.debug(`📍 ROOT.CHANGED reçu: root=${root}, skipDraw=${skipDraw}, isFanChartInitialized=${isFanChartInitialized}`);
    
    if (!isFanChartInitialized && root) {
      logger.tabs.debug("🎯 Premier ROOT.CHANGED détecté, initialisation de FanChartManager...");
      try {
        await initializeFanChartIfNeeded();
        
        // Vérifier si l'onglet Fan est visible
        const fanTab = document.querySelector('#tab1');
        if (fanTab && fanTab.classList.contains('active')) {
          logger.tabs.debug("📊 Onglet Fan visible, FanChartManager va gérer le dessin");
          // FanChartManager a son propre écouteur ROOT.CHANGED qui va dessiner le fan
          // Mais il faut s'assurer qu'il reçoit bien l'événement
          // Émettre à nouveau l'événement pour être sûr que FanChartManager le reçoive
          setTimeout(() => {
            logger.tabs.debug("🔄 Ré-émission de ROOT.CHANGED pour FanChartManager");
            storeEvents.emit(EVENTS.ROOT.CHANGED, { root, skipDraw: false });
          }, 100);
        }
      } catch (error) {
        logger.tabs.error("❌ Erreur lors de l'initialisation lazy de FanChartManager:", error);
      }
    }
  });
  
  // Stocker le disposer pour pouvoir le nettoyer si nécessaire
  window._fanChartLazyLoadingDisposer = disposer;
}

export async function initializeTabs() {
  logger.tabs.group("🚀 Initialisation des onglets");

  try {
    setupTabChangeTracking();
    setupFanChartLazyLoading();

    // Tab 1 : FanChart
    // Vérifier si tab1 est déjà visible (cas par défaut)
    const tab1Element = document.querySelector("#tab1");
    const isTab1Visible = tab1Element && (
      tab1Element.classList.contains('active') || 
      tab1Element.classList.contains('show') ||
      window.getComputedStyle(tab1Element).display !== 'none'
    );
    
    if (isTab1Visible) {
      logger.tabs.debug("📊 L'onglet Fan est actif au démarrage, chargement du HTML uniquement");
      try {
        // Charger et injecter le contenu HTML SEULEMENT
        const content = await loadTabContent('tab1');
        injectTabContent('tab1', content);
        loadedTabs.add('tab1');
        
        // NE PAS initialiser FanChartManager ici - attendre le premier fichier GEDCOM
        logger.tabs.debug("✅ Contenu HTML de l'éventail chargé (D3.js sera chargé après import GEDCOM)");
        
        // Précharger l'onglet suivant
        preloadTabContent('tab2');
      } catch (error) {
        logger.tabs.error("❌ Erreur lors du chargement du contenu de l'éventail:", error);
      }
    } else {
      // Si pas visible, utiliser l'observer pour charger le HTML
      initializeTabOnVisible("#tab1", async () => {
        logger.tabs.debug("📊 Chargement du contenu HTML de l'éventail");
        // Rien à faire ici - FanChartManager sera initialisé lors du premier rootChange
      });
    }

    // Tab 2 : Carte (avec lazy loading)
    initializeTabOnVisible("#tab2", async () => {
      logger.tabs.group("🗺️ Initialisation de la carte avec lazy loading");
      try {
        // Import dynamique des modules de la carte
        const { googleMapManager } = await import("./familyMap/managers/googleMapManager.js");
        const { googleMapsStore } = await import("./familyMap/stores/googleMapsStore.js");
        const { rootAncestorTownsStore } = await import("./familyMap/stores/rootAncestorTownsStore.js");
        
        await googleMapManager.initialize();
        await googleMapsStore.resizeAndMoveMap();
        if (rootAncestorTownsStore.hasActiveMarkers()) {
          rootAncestorTownsStore.centerMapOnAncestorMarkers();
        }
        logger.tabs.debug("✅ Carte initialisée dans tab2 (lazy loading)");
      } catch (error) {
        logger.tabs.error("❌ Erreur lors de l'initialisation de la carte:", error);
      }
      logger.tabs.groupEnd();
    });

    // Tab 3 : FamilyTree (avec lazy loading comme les autres onglets)
    initializeTabOnVisible("#tab3", async () => {
      logger.tabs.group("🌳 Initialisation de l'arbre généalogique avec lazy loading");
      try {
        // webpackPrefetch: précharge le chunk pendant l'idle time
        const { initializeFamilyTree } = await import(/* webpackPrefetch: true */ "./familyTree/familyTreeManager.js");
        await initializeFamilyTree();
        logger.tabs.debug("✅ FamilyTree2 initialisé dans tab3 (lazy loading)");
      } catch (error) {
        logger.tabs.error("❌ Erreur lors de l'initialisation de l'arbre:", error);
      }
      logger.tabs.groupEnd();
    });

    // Tab 4 : Timeline
    initializeTabOnVisible("#tab4", async () => {
      logger.tabs.group("⏳ Initialisation de la timeline");
      try {
        // Importer le gestionnaire de paramètres avant le timelineManager
        const { default: timelineParametersManager } = await import("./timeline/timelineParametersManager.js");
        const { timelineManager } = await import("./timeline/timelineManager.js");
        
        // Initialiser d'abord les paramètres pour avoir le bouton fonctionnel
        timelineParametersManager.initialize();
        logger.tabs.debug("✅ Gestionnaire de paramètres de timeline initialisé");
        
        // Vérifier si la timeline a besoin d'être mise à jour
        const { default: timelineStore } = await import("./timeline/timelineStore.js");
        if (timelineStore.needsUpdate || !timelineStore.currentTimelineHTML) {
          // La timeline a besoin d'être mise à jour
          await timelineManager.updateTimelineForRoot();
          logger.tabs.debug("✅ Timeline mise à jour");
          // Réinitialiser le flag
          timelineStore.needsUpdate = false;
        } else {
          logger.tabs.debug("✅ Timeline déjà à jour");
        }
        
        // Configurer le bouton d'options
        const optionsButton = document.getElementById('timelineOptionsButton');
        if (optionsButton) {
          optionsButton.style.display = 'block';
          logger.tabs.debug("✅ Bouton d'options de timeline configuré");
        } else {
          logger.tabs.warn("⚠️ Bouton d'options de timeline non trouvé");
        }
      } catch (error) {
        logger.tabs.error("❌ Erreur lors de l'initialisation de la timeline:", error);
      }
      logger.tabs.groupEnd();
    });

    // Tab 5 : Statistiques
    initializeTabOnVisible("#tab5", async () => {
      logger.tabs.group("📈 Initialisation des statistiques");
      try {
        const { statisticsManager } = await import(
          "./statistics/statisticsManager.js"
        );
        await statisticsManager.initialize();
        logger.tabs.debug("✅ Statistiques initialisées avec succès");
      } catch (error) {
        logger.tabs.error("❌ Erreur lors de l'initialisation des statistiques:", error);
      }
      logger.tabs.groupEnd();
    });

    // Tab 6 : Personnes (administration des personnes)
    initializeTabOnVisible("#tab6", async () => {
      logger.tabs.group("🔧 Initialisation des outils");
      try {
        const { toolsManager } = await import(
          /* webpackPrefetch: true */ "./tools/toolsManager.js"
        );
        await toolsManager.initialize();
        logger.tabs.debug("✅ Outils initialisés avec succès");
      } catch (error) {
        logger.tabs.error("❌ Erreur lors de l'initialisation des outils:", error);
      }
      logger.tabs.groupEnd();
    });

    // Tab 7 : Photos
    initializeTabOnVisible("#tab7", async () => {
      logger.tabs.group("📷 Initialisation des photos");
      try {
        const { photosTabManager } = await import("./photos/photosTabManager.js");
        await photosTabManager.initialize();
        logger.tabs.debug("✅ Photos initialisées avec succès");
      } catch (error) {
        logger.tabs.error("❌ Erreur lors de l'initialisation des photos:", error);
      }
      logger.tabs.groupEnd();
    });

    // Tab 8 : Fusion
    initializeTabOnVisible("#tab8", async () => {
      logger.tabs.group("🔀 Initialisation de la fusion");
      try {
        const { fusionManager } = await import("./fusion/fusionManager.js");
        await fusionManager.initialize();
        logger.tabs.debug("✅ Fusion initialisée avec succès");
      } catch (error) {
        logger.tabs.error("❌ Erreur lors de l'initialisation de la fusion:", error);
      }
      logger.tabs.groupEnd();
    });

    logger.tabs.debug("✅ Configuration de tous les onglets terminée");
  } catch (error) {
    logger.tabs.error(
      "❌ Erreur critique lors de l'initialisation des onglets:",
      error
    );
    throw error;
  }

  logger.tabs.groupEnd();
}
